// React e Hooks
import React from 'react';

// Material UI
import { Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';

// Recharts para visualização
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';

/**
 * Componente de gráfico adaptativo que mostra quantidade de mensagens
 * Adapta-se automaticamente ao filtro selecionado (Dia/Mês/Ano)
 */
const MessagesChart = ({
  data,
  labels,
  cor = '#6c63ff',
  totalMensagens = 0 // Novo prop para total de mensagens (opcional, pode usar o calculado)
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determina o formato do título baseado no filtro
  const getChartTitle = () => {
    return 'Histórico de Mensagens por Usuário';
  };

  // Prepara os dados para o gráfico
  const chartData = labels.map((label, index) => ({
    periodo: label,
    mensagens: data[index] || 0
  }));

  // Calcula o total de mensagens (usa o prop se fornecido, senão calcula)
  const totalMensagensCalculadas = totalMensagens || data.reduce((sum, value) => sum + value, 0);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
        overflow: 'visible',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        mx: 0,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Cabeçalho do gráfico com estatísticas */}
      <Box
        sx={{
          width: '100%',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: 'center',
            fontWeight: isMobile ? 'normal' : 'bold',
            fontSize: isMobile ? '1rem' : '1.2rem',
            mb: 1,
            color: 'text.primary',
          }}
        >
          {getChartTitle()}
        </Typography>

        {/* Estatísticas sutis no canto esquerdo */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            mb: 1,
            pl: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.90rem',
              color: 'text.secondary',
              opacity: 0.8,
            }}
          >
            {totalMensagensCalculadas > 0 && `${totalMensagensCalculadas.toLocaleString()} mensagens`}
          </Typography>
        </Box>
      </Box>

      {/* Container do gráfico com rolagem horizontal */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          overflowX: 'auto',
          overflowY: 'hidden',
          maxHeight: '500px',
        }}
      >
        <Box
          sx={{
            minWidth: { xs: 720, md: 0 },
            width: '100%',
            overflowY: 'hidden',
          }}
        >
          <ResponsiveContainer width="100%" minHeight={400}>
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 30,
              }}
            >
              <XAxis
                dataKey="periodo"
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                interval={0}
                tick={{ fill: theme.palette.text.secondary }}
                axisLine={{ stroke: theme.palette.divider }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                fontSize={12}
                tickFormatter={(v) => v.toLocaleString()}
                tick={{ fill: theme.palette.text.secondary }}
                axisLine={{ stroke: theme.palette.divider }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              {/* Tooltip customizado */}
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div style={{
                        backgroundColor: theme.palette.background.paper,
                        padding: '12px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        fontSize: '14px',
                        color: theme.palette.text.primary,
                      }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: theme.palette.text.primary }}>
                          {`Período: ${label}`}
                        </p>
                        <p style={{ fontWeight: 'bold', margin: 0, color: '#7b3ff6' }}>
                          {`Mensagens: ${payload[0].value.toLocaleString()}`}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />

              <Bar
                dataKey="mensagens"
                fill="#7b3ff6"
                radius={[4, 4, 0, 0]}
                stroke="#7b3ff6"
                strokeWidth={1}
              >

                <LabelList
                  dataKey="mensagens"
                  position="top"
                  fill={theme.palette.text.primary}
                  fontSize={18}
                  formatter={(value) => value.toLocaleString()}
                />

              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
};

export default MessagesChart;
